import { Button, Modal, ModalBody, ModalHeader, ModalFooter, Pagination, FormGroup, Label, Input, PaginationItem, PaginationLink, Table, Spinner, Container, Row, Col, Card, CardBody, PopoverBody, Popover } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaEdit, FaEllipsisH, FaFilePdf, FaPlus, FaRegBuilding, FaSearch, FaSellcast, FaTrash, FaUser, FaUsers } from "react-icons/fa";
import { supplierActions } from "../../sagas/supplierSlice";
import Swal from 'sweetalert2';
import { purchaseActions } from "../../sagas/purchaseSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { formatPrice } from "../../helpers/utility";
import { debounce } from "lodash";

const FournisseurList = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { suppliers, loading: suppliersLoading, totalPages: suppliersTotalPages, totalElements: suppliersTotalElements } = useSelector(state => state.suppliers);
    const searchInputRef = useRef(null);
    const location = useLocation();
    const [filter, setFilter] = useState('');
    const getFournisseurFormPath = () => {
        if (location.pathname.startsWith('/purchases-module')) {
            return '/purchases-module/fournisseurs/new';
        }
        return '/purchases-module/fournisseurs';
    };

    const getFournisseurEditPath = (fournisseur) => {
        if (location.pathname.startsWith('/purchases-module')) {
            return `/purchases-module/fournisseurs/${fournisseur.id}/edit`;
        }
        return '/purchases-module/fournisseurs';
    };

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [suppliers]);

    const debouncedSearch = useCallback(
        debounce((query) => {
            dispatch(supplierActions.search({
                query: query
            }));
        }, 800),
        []
    );

    useEffect(() => {
        dispatch(supplierActions.search({
            query: ""
        }));
    }, [dispatch]);

    const handleEdit = (fournisseur) => {
        const path = getFournisseurEditPath(fournisseur);
        navigate(path, { state: { fournisseur } });
    };

    const handleDeleteClient = (supId) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Le client sera désactivé et ne pourra plus être utilisé.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, désactiver !',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                let payload = {
                    id: supId,
                    onSuccess: () => {
                        Swal.fire(
                            'Inactivé !',
                            'Le client a été désactivé avec succès.',
                            'success'
                        );
                        dispatch(supplierActions.search({
                            query: ""
                        })); // Recharger la liste des clients après la suppression
                    },
                    onError: () => {
                        Swal.fire(
                            'Erreur !',
                            'Une erreur s\'est produite lors de la désactivation.',
                            'error'
                        );
                    }
                }

                dispatch(supplierActions.deleteSupplier(payload));
            }
        });
    };


    return (
        <Container fluid>
            <Row className="mb-3">
                <Col>
                    <Card className="shadow-sm border-0">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaUsers className="me-2" />
                                {t('nav.suppliers')}
                            </h4>
                            <Button color="primary" onClick={() => navigate(getFournisseurFormPath())}>
                                <FaPlus className="me-2" /> {t('fournisseur.new')}
                            </Button>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm mb-4">
                <CardBody>
                    <Row className="align-items-end">
                        <Col xs={12}>
                            <FormGroup className="mb-0">
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaSearch />
                                    </span>
                                    <Input
                                        type="text"
                                        innerRef={searchInputRef}
                                        placeholder={t('fournisseur.searchPlaceholder')}
                                        value={filter}
                                        onChange={e => {
                                            const value = e.target.value.toUpperCase();
                                            setFilter(value);
                                            debouncedSearch(value);
                                        }}
                                        className="text-uppercase"
                                        style={{ textTransform: 'uppercase' }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                debouncedSearch(e.target.value.toUpperCase());
                                            }
                                        }}
                                    />
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
            <Card className="shadow-sm mb-4">
                <CardBody className="py-3">
                    {suppliersLoading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <table className="table table-hover table-striped table-bordered" >
                                <thead className="thead-light">
                                    <tr>
                                        <th className="align-middle text-center">{t('client.nom')}</th>
                                        <th className="align-middle text-center">{t('client.totalDebit')}</th>
                                        <th className="align-middle text-center">{t('client.totalCredit')}</th>
                                        <th className="align-middle text-center">{t('client.solde')}</th>
                                        <th className="align-middle text-center">{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {suppliers.map(sup => (
                                        <tr key={sup.id}>
                                            <td>
                                                <div className="d-flex align-items-center">

                                                    <div className="me-3">
                                                        {sup.typeFournisseur === 'PARTICULIER' ? (
                                                            <FaUser className="text-primary" />
                                                        ) : (
                                                            <FaRegBuilding className="text-info" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold">
                                                            {sup.typeFournisseur === 'PARTICULIER'
                                                                ? `${sup.code} / ${sup.nom} ${sup.prenom}`
                                                                : `${sup.code} / ${sup.entreprise}`
                                                            }
                                                        </div>
                                                        <small className="text-muted">
                                                            {sup.typeClient === 'PARTICULIER' ? 'Particulier' : 'Entreprise'}
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="fw-bold">
                                                    {formatPrice(sup.totalDebit || 0)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="fw-bold">
                                                    {formatPrice(sup.totalCredit || 0)}
                                                </span>
                                            </td>
                                            <td>
                                                {Number(sup.solde) < 0 ? (
                                                    <span className="text-success fw-bold">+{formatPrice(Math.abs(sup.solde))}</span>
                                                ) : Number(sup.solde) > 0 ? (
                                                    <span className="text-danger fw-bold">-{formatPrice(Math.abs(sup.solde))}</span>
                                                ) : (
                                                    <span className="text-dark fw-bold">{formatPrice(0)}</span>
                                                )}
                                            </td>
                                            <td>
                                                <Button
                                                    color="warning"
                                                    size="sm"
                                                    onClick={() => handleEdit(sup)}
                                                >
                                                    <FaEdit />
                                                </Button>
                                                <Button
                                                    className="ms-1"
                                                    color="danger"
                                                    size="sm"
                                                    onClick={() => handleDeleteClient(sup.id)}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {(!suppliers || suppliers.length === 0) && (
                                        <tr>
                                            <td colSpan="12" className="text-center py-4">
                                                <div className="text-muted">{t('fournisseur.noData')}</div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </>)}
                </CardBody>
            </Card>
        </Container>
    );
};

export default FournisseurList;